import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useToast } from '@chakra-ui/core'
import { AppDispatch } from 'config/redux'
import { callLLMInteraction } from 'redux/actions/promts'
import { IPromptInput, isSamePromptInput } from '../model/promptInput'
import useLocaleText from 'components/useLocaleText'
import keys from 'constants/keys'
import { captureEvent } from 'services/posthog'

import { AISubtaskType, IAIGeneratedSubtask, parseGeneratedSubtask, hasGeneratedContent } from '../model/generation'

export type { AISubtaskType, IAIGeneratedSubtask } from '../model/generation'

interface IUseAISubtaskGenerationParams {
  /** Read at call time (not render time) so the latest selection is used. */
  getSubtaskType: () => AISubtaskType
  /** Receives the parsed LLM result — the caller decides where to store it. */
  onGenerated: (result: IAIGeneratedSubtask, referenceImages: string[], subtaskType: AISubtaskType) => void
  promptSubmittedEvent?: string
  generatedEvent?: string
}

export interface IAISubtaskGeneration {
  isGenerating: boolean
  /**
   * `scopeKey` identifies what the generation belongs to (e.g. a VM subtask index). The skip-if-unchanged
   * check only applies within the same scope, so the same prompt text can be generated again for a
   * different target.
   */
  generateFromPrompt: (prompt: string, referenceImages: string[], scopeKey?: string) => Promise<void>
}

/**
 * Turns a free-text prompt into a subtask title + description via the LLM.
 * Shared by AI task creation (whole task) and the VM AI-enhance sub-flow (single subtask) —
 * the only difference between them is where the result is stored, hence `onGenerated`.
 */
export const useAISubtaskGeneration = ({
  getSubtaskType,
  onGenerated,
  promptSubmittedEvent = 'ai_task_prompt_submitted',
  generatedEvent = 'ai_task_generated',
}: IUseAISubtaskGenerationParams): IAISubtaskGeneration => {
  const [isGenerating, setIsGenerating] = useState(false)

  const dispatch: AppDispatch = useDispatch()
  const toast = useToast()

  const t_llm_error = useLocaleText('llm_error')
  const t_llm_warning = useLocaleText('llm_warning')

  // Callbacks live in refs so `generateFromPrompt` stays referentially stable — it is published
  // through context and consumed by steps.
  const getSubtaskTypeRef = useRef(getSubtaskType)
  getSubtaskTypeRef.current = getSubtaskType
  const onGeneratedRef = useRef(onGenerated)
  onGeneratedRef.current = onGenerated

  const lastGeneratedInput = useRef<IPromptInput | null>(null)
  const lastGeneratedScope = useRef<string | undefined>(undefined)
  const lastGeneratedType = useRef<AISubtaskType | null>(null)
  const lastGenerationFailed = useRef(false)
  const mounted = useRef(true)
  const pending = useRef<{
    input: IPromptInput
    scopeKey?: string
    subtaskType: AISubtaskType
    promise: Promise<void>
  } | null>(null)

  const generateFromPrompt = useCallback(
    (prompt: string, referenceImages: string[], scopeKey?: string) => {
      if (!mounted.current) return Promise.resolve()

      captureEvent(promptSubmittedEvent)

      const subtaskType = getSubtaskTypeRef.current()
      const currentInput: IPromptInput = { prompt, referenceImages: [...referenceImages] }
      const activeRequest = pending.current
      if (
        activeRequest?.scopeKey === scopeKey &&
        activeRequest?.subtaskType === subtaskType &&
        isSamePromptInput(activeRequest.input, currentInput)
      )
        return activeRequest.promise
      const canSkip =
        !pending.current &&
        !lastGenerationFailed.current &&
        lastGeneratedInput.current !== null &&
        lastGeneratedScope.current === scopeKey &&
        lastGeneratedType.current === subtaskType &&
        isSamePromptInput(lastGeneratedInput.current, currentInput)

      if (canSkip) return Promise.resolve()

      const isQuestion = subtaskType === 'question'

      const request = { input: currentInput, scopeKey, subtaskType, promise: Promise.resolve() }
      pending.current = request
      const isCurrent = () => mounted.current && pending.current === request
      setIsGenerating(true)
      request.promise = (async () => {
        try {
          const payload = {
            prompt_id: isQuestion
              ? keys.AI_IMAGE_SUBTASK.CONST_QUESTION_GENERATION_PROMPT_ID
              : keys.AI_IMAGE_SUBTASK.CONST_GENERATION_PROMPT_ID,
            message: prompt,
          }
          const raw = await dispatch(callLLMInteraction(payload))
          if (!isCurrent()) return

          const result = parseGeneratedSubtask(raw)

          if (result !== null) {
            if (!hasGeneratedContent(result, subtaskType)) {
              lastGenerationFailed.current = true
              toast({
                title: t_llm_warning,
                status: 'warning',
                duration: 5000,
                isClosable: true,
              })
            } else {
              lastGeneratedInput.current = currentInput
              lastGeneratedScope.current = scopeKey
              lastGeneratedType.current = subtaskType
              lastGenerationFailed.current = false
            }

            onGeneratedRef.current(result, currentInput.referenceImages, subtaskType)
          } else {
            lastGenerationFailed.current = true
            toast({
              title: t_llm_error,
              status: 'error',
              duration: 5000,
              isClosable: true,
            })
          }
        } catch (e) {
          if (!isCurrent()) return

          console.error('LLM generation failed:', e)
          lastGenerationFailed.current = true
          toast({
            title: t_llm_error,
            status: 'error',
            duration: 5000,
            isClosable: true,
          })
        } finally {
          if (isCurrent()) {
            pending.current = null
            captureEvent(generatedEvent)
            setIsGenerating(false)
          }
        }
      })()
      return request.promise
    },
    [dispatch, toast, t_llm_error, t_llm_warning, promptSubmittedEvent, generatedEvent]
  )

  useEffect(
    () => () => {
      mounted.current = false
      pending.current = null
    },
    []
  )

  return { isGenerating, generateFromPrompt }
}
